import { Injectable, OnDestroy } from '@angular/core';
import { SvcCacheName } from './svc-cache-name.enum';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { SvcDataCache } from './svc-data-cache.model';
import { getAccessTokenSessionKey } from '../../auth/access-token-session.util';

const STORAGE_KEY_NAME = 'SvcTemporaryCacheData';
const OWN_STORAGE_KEY_NAMES: SvcCacheName[] = [SvcCacheName.TERMS, SvcCacheName.ANONYMOUS_TERMS];

const ownStorageKey = (name: SvcCacheName): string => `${STORAGE_KEY_NAME}.${name}`;

@Injectable({
  providedIn: 'root',
})
export class SvcDataCacheService implements OnDestroy {
  #destroy$: Subject<void>;
  #cache: { [key: string]: SvcDataCache<any> };

  public initializeFromAuth(signIn$: Observable<{ success: boolean }>, signOut$: Observable<{ success: boolean }>): void {
    this.ngOnDestroy();
    this.#destroy$ = new Subject<void>();
    // AB#73644 - signIn$ fires after the sign-in already loaded the user's data under the new token (dictionary,
    // languages, parameters); clearing it all made the next user fetch download it again. Only what was cached
    // under another token, or before any token, is discarded.
    signIn$
      .pipe(
        takeUntil(this.#destroy$),
        tap(() => this.clearData({ keepSession: getAccessTokenSessionKey() }))
      )
      .subscribe();
    signOut$
      .pipe(
        takeUntil(this.#destroy$),
        tap(() => this.clearData())
      )
      .subscribe();
    this.#tryToLoadStoredData();
  }

  public set<T>(name: SvcCacheName, data: T, expiresAfter?: { hours?: number }): void {
    if (data == null) {
      this.remove(name);
      return;
    }
    if (typeof this.#cache === 'object') {
      this.#cache = this.#cache ?? {};
      this.#cache[name] = new SvcDataCache({
        name,
        data,
        expiresAfter,
        session: getAccessTokenSessionKey(),
      });
      this.#persist(name);
    }
  }

  public remove(name: SvcCacheName): void {
    if (typeof this.#cache === 'object') {
      this.#cache = this.#cache ?? {};
      delete this.#cache[name];
      this.#persist(name);
    }
  }

  public get<T>(name: SvcCacheName): T {
    if (typeof this.#cache === 'object') {
      const cached = this.#cache[name];
      if (!cached?.isValid) {
        this.remove(name);
        return null;
      }
      return cached.data;
    }
    return null;
  }

  public has(name: SvcCacheName): boolean {
    if (typeof this.#cache === 'object') {
      return name in this.#cache;
    }
    return false;
  }

  public hasValue(name: SvcCacheName): boolean {
    if (typeof this.#cache === 'object') {
      const cached = this.#cache[name];
      return cached?.isValid && cached?.data != null;
    }
    return false;
  }

  public clearData(options?: { keepSession?: string | null }): void {
    const keepSession = options?.keepSession;
    const kept = Object.fromEntries(Object.entries(this.#cache ?? {}).filter(([, entry]) => !!keepSession && entry?.session === keepSession));
    localStorage.removeItem(STORAGE_KEY_NAME);
    OWN_STORAGE_KEY_NAMES.filter((name) => !(name in kept)).forEach((name) => localStorage.removeItem(ownStorageKey(name)));
    this.#cache = kept;
    if (Object.keys(kept).some((name) => !OWN_STORAGE_KEY_NAMES.includes(name as SvcCacheName))) {
      this.#writeSharedEntries();
    }
  }

  #persist(name: SvcCacheName): void {
    if (!OWN_STORAGE_KEY_NAMES.includes(name)) {
      this.#writeSharedEntries();
      return;
    }
    const entry = this.#cache[name];
    if (entry) {
      this.#write(ownStorageKey(name), entry);
    } else {
      localStorage.removeItem(ownStorageKey(name));
    }
  }

  #writeSharedEntries(): void {
    const shared = Object.fromEntries(Object.entries(this.#cache).filter(([name]) => !OWN_STORAGE_KEY_NAMES.includes(name as SvcCacheName)));
    this.#write(STORAGE_KEY_NAME, shared);
  }

  // AB#73644 - a full storage (QuotaExceededError) must not break the caller; the entry stays in memory only.
  #write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      localStorage.removeItem(key);
    }
  }

  #tryToLoadStoredData(): void {
    const cached = this.#readStoredEntries(STORAGE_KEY_NAME);
    OWN_STORAGE_KEY_NAMES.forEach((name) => {
      delete cached[name];
      const entry = this.#readStoredEntries(ownStorageKey(name), name)[name];
      if (entry) cached[name] = entry;
    });
    this.#cache = cached;
  }

  #readStoredEntries(key: string, ownName?: SvcCacheName): { [key: string]: SvcDataCache<any> } {
    const currentData = localStorage.getItem(key) ?? '{}';
    try {
      const parsed = /^{(.*)}$/g.test(currentData) ? JSON.parse(currentData) : {};
      const cached = ownName ? { [ownName]: parsed } : parsed;
      const isValidObject = (object: any) => {
        if (Array.isArray(object) || typeof object !== 'object') return false;
        const keys = Object.keys(object ?? {});
        return keys.length > 0 && ['name', 'data'].every((key) => keys.includes(key));
      };
      for (const name in cached) {
        const value = cached[name];
        if (isValidObject(value)) {
          cached[name] = SvcDataCache.fromCachedObject(value);
        } else if (ownName) {
          delete cached[name];
        } else {
          cached[name] = new SvcDataCache({
            name: name as SvcCacheName,
            data: value,
          });
        }
      }
      return cached;
    } catch {
      return {};
    }
  }

  public ngOnDestroy(): void {
    this.#destroy$?.next();
    this.#destroy$?.complete();
  }
}
